import { Button } from "@frontend/components/ui/button"
import { Contrast, Crosshair, Grid2x2, Hand, Link, MousePointer2, ZoomIn } from "lucide-react"

import type { ViewerMouseMode } from "./mouse-mode"

export interface RadiologyViewerToolbarProps {
  isBroadcasting: boolean
  onToggleBroadcasting: () => void
  mouseMode: ViewerMouseMode
  onMouseModeChange: (mode: ViewerMouseMode) => void
  crosshairVisible: boolean
  onToggleCrosshair: () => void
  onGridOpen: () => void
}

export function RadiologyViewerToolbar({
  isBroadcasting,
  onToggleBroadcasting,
  mouseMode,
  onMouseModeChange,
  crosshairVisible,
  onToggleCrosshair,
  onGridOpen
}: RadiologyViewerToolbarProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-raised p-1">
        <Button
          size="icon"
          variant={mouseMode === "pointer" ? "default" : "ghost"}
          className="size-6"
          aria-label="Pointer mode"
          title="Pointer"
          aria-pressed={mouseMode === "pointer"}
          onClick={() => onMouseModeChange("pointer")}
        >
          <MousePointer2 className="size-3.5" />
        </Button>
        <Button
          size="icon"
          variant={mouseMode === "windowLevel" ? "default" : "ghost"}
          className="size-6"
          aria-label="Window/Level mode"
          title="Window/Level"
          aria-pressed={mouseMode === "windowLevel"}
          onClick={() => onMouseModeChange("windowLevel")}
        >
          <Contrast className="size-3.5" />
        </Button>
        <Button
          size="icon"
          variant={mouseMode === "zoom" ? "default" : "ghost"}
          className="size-6"
          aria-label="Zoom mode"
          title="Zoom (3D Slicer)"
          aria-pressed={mouseMode === "zoom"}
          onClick={() => onMouseModeChange("zoom")}
        >
          <ZoomIn className="size-3.5" />
        </Button>
        <Button
          size="icon"
          variant={mouseMode === "pan" ? "default" : "ghost"}
          className="size-6"
          aria-label="Pan mode"
          title="Pan"
          aria-pressed={mouseMode === "pan"}
          onClick={() => onMouseModeChange("pan")}
        >
          <Hand className="size-3.5" />
        </Button>
      </div>

      <Button
        size="icon"
        variant={isBroadcasting ? "default" : "ghost"}
        className="size-6"
        aria-label={isBroadcasting ? "Sync on" : "Sync off"}
        title={isBroadcasting ? "Sync on" : "Sync off"}
        aria-pressed={isBroadcasting}
        onClick={onToggleBroadcasting}
      >
        <Link className="size-3.5" />
      </Button>

      <Button
        size="icon"
        variant={crosshairVisible ? "default" : "ghost"}
        className="size-6"
        aria-label={crosshairVisible ? "Crosshair on" : "Crosshair off"}
        title={crosshairVisible ? "Crosshair on" : "Crosshair off"}
        aria-pressed={crosshairVisible}
        onClick={onToggleCrosshair}
      >
        <Crosshair className="size-3.5" />
      </Button>

      <div className="flex-1" />

      <Button
        size="icon"
        variant="ghost"
        className="size-6"
        aria-label="Hanging protocols"
        title="Hanging protocols"
        onClick={onGridOpen}
      >
        <Grid2x2 className="size-3.5" />
      </Button>
    </div>
  )
}
